/**
 * Panneau d'un POI « Escalade » : onglets Fiche (le POI), Parois et Voies, tout relié au même site
 * (comme Mountain Project ou theCrag : site → paroi → voie). Les parois se placent et leur pied de
 * paroi se trace sur la carte ; les voies se placent en cliquant le long du pied de paroi.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CLIMBING_STYLES, type ClimbingCrag, type ClimbingRoute, type ClimbingStyle } from '../../../api'
import { EMPTY_ROUTE, routesAlong, type ClimbingEditor, type RouteDraft } from './useClimbingEditor'

type Props = { editor: ClimbingEditor; fiche: ReactNode; onFocus: (points: { lat: number; lng: number }[]) => void }

export function ClimbingPanel({ editor, fiche, onFocus }: Props) {
  const { t } = useTranslation('gestion')
  const { tab, setTab, crags, site } = editor
  const routeCount = crags.reduce((n, c) => n + c.routes.length, 0)
  const tabs: { key: typeof tab; label: string }[] = [
    { key: 'fiche', label: t('escalade.onglet_fiche') },
    { key: 'parois', label: site ? t('escalade.onglet_parois_n', { count: crags.length }) : t('escalade.onglet_parois') },
    { key: 'voies', label: site ? t('escalade.onglet_voies_n', { count: routeCount }) : t('escalade.onglet_voies') },
  ]
  return (
    <div className="g-climb">
      <div className="g-segmented g-segmented--full g-climb__tabs" role="tablist">
        {tabs.map((x) => (
          <button
            key={x.key}
            type="button"
            role="tab"
            aria-selected={tab === x.key}
            className={`g-segmented__item${tab === x.key ? ' g-segmented__item--active' : ''}`}
            onClick={() => {
              editor.setTool(null)
              setTab(x.key)
            }}
          >
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'fiche' ? fiche : <SiteGate editor={editor}>{tab === 'parois' ? <CragsTab editor={editor} onFocus={onFocus} /> : <RoutesTab editor={editor} />}</SiteGate>}
    </div>
  )
}

/** Pas encore de site : le créer, ou relier celui qu'OSM a importé juste à côté. */
function SiteGate({ editor, children }: { editor: ClimbingEditor; children: ReactNode }) {
  const { t } = useTranslation('gestion')
  const { site, linked, loading, busy, error, actions } = editor
  if (loading) return <p className="g-hint g-editor__details">{t('commun.chargement')}</p>
  return (
    <div className="g-editor__details">
      {error && <p className="g-alert g-alert--error">{error}</p>}
      {!site && (
        <>
          <p className="g-hint">{t('escalade.aucun_site')}</p>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void actions.createSite()}>
            <span className="mdi mdi-plus" aria-hidden /> {t('escalade.creer_site')}
          </button>
        </>
      )}
      {site && !linked && (
        <>
          <p className="g-hint">{t('escalade.site_osm_proche', { name: site.name, count: site.crags.length })}</p>
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void actions.linkSite()}>
            <span className="mdi mdi-link-variant" aria-hidden /> {t('escalade.gerer_site')}
          </button>
        </>
      )}
      {site && linked && children}
    </div>
  )
}

/* ---------- Parois ---------- */

function CragsTab({ editor, onFocus }: { editor: ClimbingEditor; onFocus: Props['onFocus'] }) {
  const { t } = useTranslation('gestion')
  const { crags, crag, tool, setTool, setCragId, busy } = editor
  const [newName, setNewName] = useState('')

  if (crag) return <CragForm key={crag.id} editor={editor} crag={crag} />

  return (
    <>
      <h2 className="g-editor__section">{t('escalade.parois')}</h2>
      {crags.length === 0 ? (
        <p className="g-hint">{t('escalade.aucune_paroi')}</p>
      ) : (
        <ul className="g-editor__list">
          {crags.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                className="g-editor__item"
                onClick={() => {
                  setCragId(c.id)
                  onFocus([...(c.location ? [c.location] : []), ...c.cliff_lines.flat()])
                }}
              >
                <span className="mdi mdi-image-filter-hdr g-climb__icon" aria-hidden />
                <span className="g-editor__item-text">
                  <span>{c.name || t('escalade.sans_nom')}</span>
                  <span className="g-muted">
                    {t('escalade.n_voies', { count: c.routes.length })}
                    {c.cliff_lines.length === 0 && ` · ${t('escalade.sans_pied')}`}
                    {c.from_osm && ' · OSM'}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="g-climb__add">
        <label className="g-field">
          <span className="g-field__label">{t('escalade.nouvelle_paroi')}</span>
          <input className="g-input" value={newName} placeholder={t('escalade.nom_paroi')} onChange={(e) => setNewName(e.target.value)} />
        </label>
        {tool?.kind === 'crag' ? (
          <p className="g-climb__banner">
            {t('escalade.cliquer_paroi')}{' '}
            <button type="button" className="g-link" onClick={() => setTool(null)}>
              {t('commun.annuler')}
            </button>
          </p>
        ) : (
          <button type="button" className="btn" disabled={busy || !newName.trim()} onClick={() => setTool({ kind: 'crag', name: newName.trim() })}>
            <span className="mdi mdi-map-marker-plus" aria-hidden /> {t('escalade.placer_carte')}
          </button>
        )}
      </div>
    </>
  )
}

function CragForm({ editor, crag }: { editor: ClimbingEditor; crag: ClimbingCrag }) {
  const { t } = useTranslation('gestion')
  const { tool, setTool, setCragId, setTab, busy, actions } = editor
  const [name, setName] = useState(crag.name)
  const [description, setDescription] = useState(crag.description)
  const [url, setUrl] = useState(crag.url)
  const dirty = name !== crag.name || description !== crag.description || url !== crag.url
  const drawing = tool?.kind === 'line' && tool.cragId === crag.id

  return (
    <>
      <button type="button" className="g-back" onClick={() => (setTool(null), setCragId(null))}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('escalade.retour_parois')}
      </button>
      <h2 className="g-editor__section">{crag.name || t('escalade.sans_nom')}</h2>
      {crag.from_osm && <p className="g-hint">{t('escalade.vient_osm')}</p>}
      <label className="g-field">
        <span className="g-field__label">{t('carte.nom')}</span>
        <input className="g-input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="g-field">
        <span className="g-field__label">{t('escalade.description')}</span>
        <textarea className="g-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label className="g-field">
        <span className="g-field__label">{t('escalade.lien')}</span>
        <input className="g-input" type="url" value={url} placeholder="https://www.mountainproject.com/area/…" onChange={(e) => setUrl(e.target.value)} />
      </label>

      <div className="g-field">
        <span className="g-field__label">{t('escalade.pied_paroi')}</span>
        {drawing ? (
          <p className="g-climb__banner">
            {t('escalade.tracer_aide', { count: tool.points.length })}{' '}
            <button type="button" className="g-link" disabled={tool.points.length < 2 || busy} onClick={() => void actions.finishLine()}>
              {t('escalade.terminer')}
            </button>{' '}
            <button type="button" className="g-link" onClick={() => setTool(null)}>
              {t('commun.annuler')}
            </button>
          </p>
        ) : (
          <div className="g-editor__buttons">
            <button type="button" className="btn" disabled={busy} onClick={() => setTool({ kind: 'line', cragId: crag.id, points: [] })}>
              <span className="mdi mdi-vector-polyline-plus" aria-hidden /> {crag.cliff_lines.length ? t('escalade.ajouter_ligne') : t('escalade.tracer_pied')}
            </button>
            {crag.cliff_lines.length > 0 && (
              <button type="button" className="btn g-danger" disabled={busy} onClick={() => window.confirm(t('escalade.effacer_lignes_confirm')) && void actions.clearLines(crag.id)}>
                {t('escalade.effacer_lignes')}
              </button>
            )}
          </div>
        )}
        <span className="g-hint">{t('escalade.aide_pied')}</span>
      </div>

      {tool?.kind === 'move-crag' && tool.cragId === crag.id ? (
        <p className="g-climb__banner">
          {t('escalade.cliquer_deplacer')}{' '}
          <button type="button" className="g-link" onClick={() => setTool(null)}>
            {t('commun.annuler')}
          </button>
        </p>
      ) : (
        <button type="button" className="btn" disabled={busy} onClick={() => setTool({ kind: 'move-crag', cragId: crag.id })}>
          <span className="mdi mdi-cursor-move" aria-hidden /> {t('escalade.deplacer_repere')}
        </button>
      )}

      <div className="g-editor__buttons">
        <button type="button" className="btn btn--primary" disabled={busy || !dirty || !name.trim()} onClick={() => void actions.saveCrag(crag.id, { name: name.trim(), description, url: url.trim() })}>
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
        <button type="button" className="btn" onClick={() => setTab('voies')}>
          {t('escalade.voir_voies', { count: crag.routes.length })}
        </button>
        <span className="g-spacer" />
        <button
          type="button"
          className="btn g-danger"
          disabled={busy}
          onClick={() => window.confirm(t('escalade.supprimer_paroi_confirm', { count: crag.routes.length })) && void actions.removeCrag(crag.id)}
        >
          <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
        </button>
      </div>
    </>
  )
}

/* ---------- Voies ---------- */

function RoutesTab({ editor }: { editor: ClimbingEditor }) {
  const { t } = useTranslation('gestion')
  const { crags, crag, setCragId, routeId, setRouteId, setTool } = editor
  useEffect(() => {
    if (!crag && crags.length > 0) setCragId(crags[0].id)
  }, [crag, crags, setCragId])

  if (crags.length === 0) return <p className="g-hint">{t('escalade.voies_sans_paroi')}</p>
  const route = crag && typeof routeId === 'number' ? crag.routes.find((r) => r.id === routeId) : undefined

  return (
    <>
      <label className="g-field">
        <span className="g-field__label">{t('escalade.paroi')}</span>
        <select
          className="g-input"
          value={crag?.id ?? ''}
          onChange={(e) => {
            setTool(null)
            setRouteId(null)
            setCragId(Number(e.target.value))
          }}
        >
          {crags.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || t('escalade.sans_nom')} ({c.routes.length})
            </option>
          ))}
        </select>
      </label>
      {crag && routeId === 'new' && <RouteForm key="new" editor={editor} crag={crag} />}
      {crag && route && <RouteForm key={route.id} editor={editor} crag={crag} route={route} />}
      {crag && routeId == null && (
        <>
          {crag.cliff_lines.length === 0 && <p className="g-hint">{t('escalade.conseil_pied')}</p>}
          {crag.routes.length === 0 ? (
            <p className="g-hint">{t('escalade.aucune_voie')}</p>
          ) : (
            <ol className="g-editor__list g-climb__routes">
              {routesAlong(crag).map((r, i) => (
                <li key={r.id}>
                  <button type="button" className="g-editor__item" onClick={() => setRouteId(r.id)}>
                    <span className="g-climb__num">{i + 1}</span>
                    <span className="g-editor__item-text">
                      <span>
                        {r.name || t('escalade.sans_nom')} {r.grade && <strong className="g-climb__grade">{r.grade}</strong>}
                      </span>
                      <span className="g-muted">{[r.styles.map((s) => t(`escalade.style_${s}`)).join(', '), r.length_m ? `${Math.round(r.length_m)} m` : ''].filter(Boolean).join(' · ')}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
          <button type="button" className="btn btn--primary" onClick={() => setRouteId('new')}>
            <span className="mdi mdi-plus" aria-hidden /> {t('escalade.ajouter_voie')}
          </button>
        </>
      )}
    </>
  )
}

function draftOf(route?: ClimbingRoute): RouteDraft {
  if (!route) return EMPTY_ROUTE
  const { name, grade, styles, length_m, pitches, bolts, description } = route
  return { name, grade, styles, length_m, pitches, bolts, description }
}

function RouteForm({ editor, crag, route }: { editor: ClimbingEditor; crag: ClimbingCrag; route?: ClimbingRoute }) {
  const { t } = useTranslation('gestion')
  const { tool, setTool, setRouteId, busy, actions } = editor
  const [draft, setDraft] = useState<RouteDraft>(draftOf(route))
  const initial = draftOf(route)
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial)
  const placing = tool?.kind === 'route' || (tool?.kind === 'move-route' && tool.routeId === route?.id)
  const set = <K extends keyof RouteDraft>(key: K, value: RouteDraft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const num = (v: string) => (v.trim() === '' ? null : Number(v))
  const toggleStyle = (s: ClimbingStyle) => set('styles', draft.styles.includes(s) ? draft.styles.filter((x) => x !== s) : [...draft.styles, s])

  return (
    <>
      <button type="button" className="g-back" onClick={() => (setTool(null), setRouteId(null))}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('escalade.retour_voies', { name: crag.name || t('escalade.sans_nom') })}
      </button>
      <h2 className="g-editor__section">{route ? route.name || t('escalade.sans_nom') : t('escalade.nouvelle_voie')}</h2>
      {route?.from_osm && <p className="g-hint">{t('escalade.vient_osm')}</p>}
      <label className="g-field">
        <span className="g-field__label">{t('carte.nom')}</span>
        <input className="g-input" value={draft.name} onChange={(e) => set('name', e.target.value)} />
      </label>
      <div className="g-climb__row">
        <label className="g-field">
          <span className="g-field__label">{t('escalade.cote')}</span>
          <input className="g-input" value={draft.grade} placeholder="5.10b, 6a+, V4…" onChange={(e) => set('grade', e.target.value)} />
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('escalade.longueur_m')}</span>
          <input className="g-input" type="number" min={0} value={draft.length_m ?? ''} onChange={(e) => set('length_m', num(e.target.value))} />
        </label>
      </div>
      <div className="g-climb__row">
        <label className="g-field">
          <span className="g-field__label">{t('escalade.longueurs')}</span>
          <input className="g-input" type="number" min={1} value={draft.pitches ?? ''} onChange={(e) => set('pitches', num(e.target.value))} />
        </label>
        <label className="g-field">
          <span className="g-field__label">{t('escalade.ancrages')}</span>
          <input className="g-input" type="number" min={0} value={draft.bolts ?? ''} onChange={(e) => set('bolts', num(e.target.value))} />
        </label>
      </div>
      <div className="g-field">
        <span className="g-field__label">{t('escalade.styles')}</span>
        <div className="g-chips" role="group" aria-label={t('escalade.styles')}>
          {CLIMBING_STYLES.map((s) => {
            const on = draft.styles.includes(s)
            return (
              <button key={s} type="button" aria-pressed={on} className={`g-chip g-chip--sm${on ? ' g-chip--active' : ''}`} onClick={() => toggleStyle(s)}>
                {on && <span className="mdi mdi-check" aria-hidden />}
                {t(`escalade.style_${s}`)}
              </button>
            )
          })}
        </div>
      </div>
      <label className="g-field">
        <span className="g-field__label">{t('escalade.description')}</span>
        <textarea className="g-input" rows={3} value={draft.description} onChange={(e) => set('description', e.target.value)} />
      </label>

      {placing ? (
        <p className="g-climb__banner">
          {t(crag.cliff_lines.length ? 'escalade.cliquer_voie_pied' : 'escalade.cliquer_voie')}{' '}
          <button type="button" className="g-link" onClick={() => setTool(null)}>
            {t('commun.annuler')}
          </button>
        </p>
      ) : route ? (
        <div className="g-editor__buttons">
          <button type="button" className="btn btn--primary" disabled={busy || !dirty || !draft.name.trim()} onClick={() => void actions.saveRoute(crag.id, route.id, { ...draft, name: draft.name.trim() })}>
            {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={() => setTool({ kind: 'move-route', routeId: route.id, cragId: crag.id })}>
            <span className="mdi mdi-cursor-move" aria-hidden /> {t('escalade.deplacer')}
          </button>
          <span className="g-spacer" />
          <button type="button" className="btn g-danger" disabled={busy} onClick={() => window.confirm(t('escalade.supprimer_voie_confirm')) && void actions.removeRoute(crag.id, route.id)}>
            <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
          </button>
        </div>
      ) : (
        <button type="button" className="btn btn--primary" disabled={busy || !draft.name.trim()} onClick={() => setTool({ kind: 'route', cragId: crag.id, draft: { ...draft, name: draft.name.trim() } })}>
          <span className="mdi mdi-map-marker-plus" aria-hidden /> {t('escalade.placer_carte')}
        </button>
      )}
    </>
  )
}
